import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack, useTheme} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CelebrateLine, DropboxLine, OutOfOfficeLine} from '@dropbox/dig-icons/assets';
import {Avatar} from 'components/DSYS/Avatar';
import {Facepile} from 'components/DSYS/Facepile';
import {Link} from 'components/DSYS/Link';
import {ROUTE_PATHS} from 'constant';
import {format} from 'date-fns';
import {CSSProperties, MouseEvent, useEffect, useMemo, useRef, useState} from 'react';
import {logDashboardInteraction} from 'views/home/dashboard/dashboardAnalytics';

import {
  calendarDayTooltipDayKey,
  calendarDayTooltipPersonCount,
  calendarDayTooltipTarget,
} from './calendarDayTooltipAnalytics';
import {buildPtoFillRamp, ptoFillForLayer, PtoThemeMode} from './calendarFills';
import {
  AnniversaryPerson,
  anniversaryYearsLabel,
  BirthdayPerson,
  CalendarDay,
  PtoPerson,
  spanLabel,
} from './teamCalendar';
import styles from './TeamCalendar.module.css';

// Sunday-first weekday initials; rotated to match the viewer's week-start choice.
const SUNDAY_FIRST_WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const weekdayLabels = (weekStartsOn: 0 | 1): string[] => [
  ...SUNDAY_FIRST_WEEKDAYS.slice(weekStartsOn),
  ...SUNDAY_FIRST_WEEKDAYS.slice(0, weekStartsOn),
];

const shapeClassName = (day: CalendarDay): string =>
  [styles.day, day.isToday ? styles.dayToday : '', day.isOut ? styles.dayOut : '']
    .filter(Boolean)
    .join(' ');

const dayPills = (day: CalendarDay, overlapLayers: Map<string, number>, ptoRamp: string[]) => {
  const ordered = [...day.segments].sort(
    (left, right) =>
      (overlapLayers.get(left.person.id) ?? 0) - (overlapLayers.get(right.person.id) ?? 0)
  );
  return ordered.map((segment) => {
    const layer = overlapLayers.get(segment.person.id) ?? 0;
    return (
      <div
        key={segment.person.id}
        className={[
          styles.segment,
          segment.isStart ? styles.segmentStart : '',
          segment.isEnd ? styles.segmentEnd : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={
          {
            backgroundColor: ptoFillForLayer(layer, ptoRamp),
          } as CSSProperties
        }
      />
    );
  });
};

// One tooltip row mirrors a list-mode row: name, "•"-joined detail parts, type icon.
const tooltipRow = (
  key: string,
  name: string,
  parts: Array<string | null>,
  icon: React.ComponentType<React.SVGAttributes<SVGElement>>
) => (
  <div key={key} className={atoms({display: 'flex', alignItems: 'center'})}>
    <span>{[name, ...parts].filter(Boolean).join(' • ')}</span>
    <UIIcon src={icon} className={styles.tooltipInlineIcon} aria-hidden />
  </div>
);

const dayTooltip = (day: CalendarDay) => {
  // Every birthday/anniversary row in this tooltip lands on the hovered cell, so
  // the day label is just the cell's own date (e.g. "Jun 12").
  const dayLabel = format(day.date, 'MMM d');

  return (
    <Stack gap="4" className={atoms({textAlign: 'left'})}>
      {day.people.map((person) =>
        tooltipRow(person.id, person.name, [spanLabel(person)], OutOfOfficeLine)
      )}
      {day.birthdays.map((person) =>
        tooltipRow(`bday-${person.id}`, person.name, [dayLabel], CelebrateLine)
      )}
      {day.anniversaries.map((person) =>
        tooltipRow(
          `anniv-${person.id}`,
          person.name,
          [dayLabel, anniversaryYearsLabel(person.years)],
          DropboxLine
        )
      )}
    </Stack>
  );
};

const profilePath = (ldap: string) => ROUTE_PATHS.PROFILE.replace(':userid', ldap);

const celebrationPeople = (
  birthdays: BirthdayPerson[],
  anniversaries: AnniversaryPerson[]
): Array<BirthdayPerson | AnniversaryPerson> => {
  const peopleByLdap = new Map<string, BirthdayPerson | AnniversaryPerson>();
  for (const person of [...birthdays, ...anniversaries]) {
    peopleByLdap.set(person.ldap, person);
  }
  return [...peopleByLdap.values()];
};

const CelebrationPeople = ({
  people,
  onProfileLinkClick,
}: {
  people: Array<BirthdayPerson | AnniversaryPerson>;
  onProfileLinkClick?: (ldap: string) => void;
}) => {
  const visiblePeople = people.slice(0, 2);
  const overflowCount = people.length - visiblePeople.length;

  return (
    <div className={styles.celebrationPeople}>
      {visiblePeople.map((person) => (
        <Link
          key={person.id}
          to={profilePath(person.ldap)}
          aria-label={person.name}
          hasNoUnderline
          monochromatic
          className={styles.celebrationPersonLink}
          onClick={() => onProfileLinkClick?.(person.ldap)}
        >
          <Avatar
            className={styles.celebrationAvatar}
            size="xsmall"
            user={{name: person.name, ldap: person.ldap, profile_photo_url: person.photoUrl}}
          />
        </Link>
      ))}
      {overflowCount > 0 ? (
        <span className={styles.celebrationOverflow}>+{overflowCount}</span>
      ) : null}
    </div>
  );
};

/**
 * Condensed layout for today cells that have BOTH celebrations AND PTO starting.
 * Rules:
 * - 1 celebration + >1 PTO → celebration avatar + "+N" for PTO
 * - >1 celebrations + 1 PTO → PTO avatar + "+N" for celebrations
 * - >1 celebrations + >1 PTO → "+N" "+N" (two overflow badges)
 */
const CondensedTodayContent = ({
  celebrations,
  ptoStarting,
  hasBirthday,
  hasAnniversary,
  onProfileLinkClick,
}: {
  celebrations: Array<BirthdayPerson | AnniversaryPerson>;
  ptoStarting: PtoPerson[];
  hasBirthday: boolean;
  hasAnniversary: boolean;
  onProfileLinkClick?: (ldap: string) => void;
}) => {
  const celebCount = celebrations.length;
  const ptoCount = ptoStarting.length;

  if (celebCount === 1 && ptoCount > 1) {
    return (
      <Box display="inline-flex" alignItems="center" className={styles.condensedToday}>
        <Box
          className={[
            styles.celebrationContent,
            hasBirthday ? styles.birthdayContent : '',
            hasAnniversary && !hasBirthday ? styles.anniversaryContent : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <CelebrationPeople people={celebrations} onProfileLinkClick={onProfileLinkClick} />
        </Box>
        <span className={styles.celebrationOverflow}>+{ptoCount}</span>
      </Box>
    );
  }

  if (celebCount > 1 && ptoCount === 1) {
    return (
      <Box display="inline-flex" alignItems="center" className={styles.condensedToday}>
        <Facepile
          ldaps={[ptoStarting[0].ldap]}
          size="xsmall"
          overflow={2}
          shouldSort={false}
          hideTooltip
          linkToProfile
          onProfileLinkClick={onProfileLinkClick}
        />
        <span className={styles.celebrationOverflow}>+{celebCount}</span>
      </Box>
    );
  }

  // Both >1: two overflow badges
  return (
    <Box display="inline-flex" alignItems="center" className={styles.condensedToday}>
      <span className={styles.celebrationOverflow}>+{celebCount}</span>
      <span className={styles.celebrationOverflow}>+{ptoCount}</span>
    </Box>
  );
};

const dayCell = (
  day: CalendarDay,
  key: string,
  index: number,
  overlapLayers: Map<string, number>,
  ptoRamp: string[],
  onProfileLinkClick?: (ldap: string) => void
) => {
  // Show celebration people inside the celebration marker. Otherwise anchor PTO
  // avatars at the start of each visible run; other out-days keep the date number.
  const startLdaps = day.startingPeople.map((person) => person.ldap).filter(Boolean);
  const hasBirthday = day.birthdays.length > 0;
  const hasAnniversary = day.anniversaries.length > 0;
  const isCelebration = hasBirthday || hasAnniversary;
  const markedCelebrationPeople = celebrationPeople(day.birthdays, day.anniversaries);

  // When today has both celebrations AND PTO starting, the today ring constrains
  // available width. Use a condensed layout when either side has >1 person.
  const needsCondensed =
    day.isToday &&
    isCelebration &&
    startLdaps.length > 0 &&
    (markedCelebrationPeople.length > 1 || startLdaps.length > 1);

  return (
    <Box
      key={key}
      role="gridcell"
      data-day-index={index}
      display="flex"
      alignItems="center"
      justifyContent="center"
      className={[shapeClassName(day), day.isOut || isCelebration ? atoms({cursor: 'default'}) : '']
        .filter(Boolean)
        .join(' ')}
    >
      {day.isOut ? dayPills(day, overlapLayers, ptoRamp) : null}
      <Box className={styles.dayContent}>
        {needsCondensed ? (
          <CondensedTodayContent
            celebrations={markedCelebrationPeople}
            ptoStarting={day.startingPeople}
            hasBirthday={hasBirthday}
            hasAnniversary={hasAnniversary}
            onProfileLinkClick={onProfileLinkClick}
          />
        ) : isCelebration ? (
          <Box
            className={[
              styles.celebrationContent,
              hasBirthday ? styles.birthdayContent : '',
              hasAnniversary && !hasBirthday ? styles.anniversaryContent : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <CelebrationPeople
              people={markedCelebrationPeople}
              onProfileLinkClick={onProfileLinkClick}
            />
          </Box>
        ) : startLdaps.length === 0 ? (
          <Text
            size="small"
            isBold={day.isToday}
            color={day.isPast ? 'disabled' : !day.isOut && !day.inMonth ? 'subtle' : undefined}
          >
            {day.date.getDate()}
          </Text>
        ) : null}
        {startLdaps.length > 0 && !needsCondensed ? (
          <Facepile
            ldaps={startLdaps}
            size="xsmall"
            overflow={2}
            shouldSort={false}
            hideTooltip
            linkToProfile
            onProfileLinkClick={onProfileLinkClick}
          />
        ) : null}
      </Box>
    </Box>
  );
};

// Zero-size rect at the cursor, used as the tooltip's virtual anchor so it
// tracks the pointer instead of snapping to cell centers.
const cursorRect = (x: number, y: number): DOMRect =>
  ({
    width: 0,
    height: 0,
    top: y,
    bottom: y,
    left: x,
    right: x,
    x,
    y,
    toJSON: () => ({}),
  }) as DOMRect;

/** Month grid (weekday header + 6 week rows). */
export const MiniTeamCalendar = ({
  days,
  overlapLayers,
  weekStartsOn = 0,
  onProfileLinkClick,
}: {
  days: CalendarDay[];
  overlapLayers: Map<string, number>;
  /** Sunday (0) or Monday (1); rotates the weekday header to match the grid. */
  weekStartsOn?: 0 | 1;
  onProfileLinkClick?: (ldap: string) => void;
}) => {
  const weekdays = weekdayLabels(weekStartsOn);
  const {mode} = useTheme();
  const ptoTheme: PtoThemeMode = mode === 'dark' ? 'dark' : 'light';
  const ptoRamp = useMemo(() => buildPtoFillRamp(ptoTheme), [ptoTheme]);
  // One shared tooltip anchored to a cursor-following virtual element, so moving
  // across a span repositions a single tooltip instead of mounting many.
  const pointer = useRef({x: 0, y: 0});
  const anchorRef = useRef({
    getBoundingClientRect: () => cursorRect(pointer.current.x, pointer.current.y),
  });
  const tooltipRef = useRef<{update: () => void}>(null);
  const [hoveredDay, setHoveredDay] = useState<CalendarDay | null>(null);
  const closeTimer = useRef<number | null>(null);
  // Retain the last day so the tooltip body stays rendered while it animates
  // closed, instead of the text vanishing before the surface/nib fade out.
  const lastHoveredDay = useRef<CalendarDay | null>(null);
  const loggedTooltipDays = useRef(new Set<string>());
  if (hoveredDay) lastHoveredDay.current = hoveredDay;

  const clearCloseTimer = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleTooltipClose = () => {
    if (closeTimer.current !== null) {
      return;
    }
    closeTimer.current = window.setTimeout(() => {
      setHoveredDay(null);
      closeTimer.current = null;
    }, 100);
  };

  const showTooltipForDay = (day: CalendarDay) => {
    clearCloseTimer();

    const dayKey = calendarDayTooltipDayKey(day);
    const isFirstTooltipForDay = !loggedTooltipDays.current.has(dayKey);
    if (isFirstTooltipForDay) {
      loggedTooltipDays.current.add(dayKey);
      logDashboardInteraction({
        action: 'calendar_day_tooltip_show',
        tileId: 'calendar',
        target: calendarDayTooltipTarget(day),
        value: String(calendarDayTooltipPersonCount(day)),
      });
    }

    setHoveredDay((currentDay) => (currentDay === day ? currentDay : day));
  };

  useEffect(
    () => () => {
      clearCloseTimer();
    },
    []
  );

  // Track hover at the grid level, not per cell: one continuous `mousemove` on a
  // single element avoids the mouseenter/mouseleave churn that flickered the
  // tooltip at cell boundaries. The cell under the cursor comes from data-day-index.
  const handleGridMove = (event: MouseEvent<HTMLDivElement>) => {
    pointer.current = {x: event.clientX, y: event.clientY};
    tooltipRef.current?.update();

    // Over a gap between cells: keep the current tooltip and let it follow the cursor.
    const cell = (event.target as HTMLElement).closest<HTMLElement>('[data-day-index]');
    if (!cell) return;

    const dayIndex = Number(cell.dataset.dayIndex);
    const day = Number.isNaN(dayIndex) ? null : days[dayIndex];
    if (day && (day.isOut || day.birthdays.length > 0 || day.anniversaries.length > 0)) {
      showTooltipForDay(day);
      return;
    }

    scheduleTooltipClose();
  };

  return (
    <Box style={{marginInline: -8, height: '100%', display: 'flex', flexDirection: 'column'}}>
      <Box className={styles.weekRow} aria-hidden>
        {weekdays.map((label, index) => (
          <Text
            key={index}
            size="xsmall"
            color="faint"
            tagName="span"
            isBold
            className={[styles.weekday, atoms({textAlign: 'center'})].join(' ')}
          >
            {label}
          </Text>
        ))}
      </Box>

      <Tooltip.Control
        ref={tooltipRef}
        open={Boolean(hoveredDay)}
        triggerRef={anchorRef}
        placement="top"
        offsetDistance={0}
        className={styles.tooltip}
      >
        {lastHoveredDay.current ? dayTooltip(lastHoveredDay.current) : null}
      </Tooltip.Control>

      <Box
        className={styles.grid}
        role="grid"
        style={{flex: 1, minHeight: 0}}
        onMouseMove={handleGridMove}
        onMouseLeave={scheduleTooltipClose}
      >
        {days.map((day, index) =>
          dayCell(day, day.date.toISOString(), index, overlapLayers, ptoRamp, onProfileLinkClick)
        )}
      </Box>
    </Box>
  );
};
